'use client';

import React, { useMemo, useState } from "react";
import { AlertCircle, UserPlus, CheckCircle2, IndianRupee, Target, ArrowUp, ArrowDown, Minus, ChevronRight } from "lucide-react";
import { useJigyasa, ownerMetrics, formatINR, filterCallBackToday } from "./state";
import { Card, SectionTitle, formatDate, LineSwitcher } from "./parts";
import { EnquiryDetailSheet, Sheet } from "./sheets";
import { EnquiryRow } from "./rows";
import { OwnerCapacityStrip, OwnerRenewalRadar } from "./intelligence";
import { TrendLineChart } from "./charts";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const SOURCE_COLORS = ["#78716c", "#a8a29e", "#d97706", "#65a30d", "#0891b2", "#7c3aed", "#db2777", "#dc2626", "#0284c7", "#84cc16"];
const LOST_COLORS = ["#dc2626", "#ea580c", "#ca8a04", "#65a30d", "#0891b2", "#7c3aed", "#db2777", "#525252"];

export const OwnerPhone = () => {
  const { enquiries, lineEnquiries, costume, companyInfo } = useJigyasa();
  const m = useMemo(() => ownerMetrics(lineEnquiries), [lineEnquiries]);
  const [selectedId, setSelectedId] = useState(null);
  const [drill, setDrill] = useState(null); // { title, subtitle, items } | null
  const selected = enquiries.find((e) => e.id === selectedId) || null;
  const c = costume.copy;

  // "Needs your attention" — pilot feedback, 11 Jun 2026.
  const overdueList = useMemo(() => filterCallBackToday(lineEnquiries), [lineEnquiries]);
  const waitingList = useMemo(
    () => lineEnquiries.filter((e) => e.stage === "sent_price"),
    [lineEnquiries]
  );
  const overdue = overdueList.length;
  const waitingOffer = waitingList.length;
  const attentionCount = overdue + waitingOffer;

  const winRatePct = m.newThisMonth > 0 ? Math.round((m.signedCountThis / m.newThisMonth) * 100) : null;

  const openDrill = (title, subtitle, items) => setDrill({ title, subtitle, items });

  return (
    <div className="mx-auto max-w-md px-4 pb-12 pt-4">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Your business, today</h1>
        <p className="text-sm text-zinc-500">{companyInfo.name}</p>
      </div>

      <LineSwitcher />

      {/* Needs your attention */}
      <Card
        data-testid="owner-attention-strip"
        className={`mb-5 ${attentionCount > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}
      >
        <div className={`mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${attentionCount > 0 ? "text-amber-800" : "text-emerald-800"}`}>
          <AlertCircle size={14} /> {c.sectionAttention}
        </div>
        {attentionCount === 0 ? (
          <p data-testid="owner-attention-clear" className="text-sm text-emerald-900">{c.attentionAllClear}</p>
        ) : (
          <div data-testid="owner-attention-items" className="flex flex-wrap gap-2">
            {overdue > 0 && (
              <button
                type="button"
                data-testid="owner-attention-overdue"
                onClick={() => openDrill(
                  "Follow-ups overdue",
                  "Call these today so no lead goes cold.",
                  overdueList,
                )}
                className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-sm font-semibold text-amber-900 shadow-sm ring-1 ring-amber-200 hover:bg-amber-100"
              >
                {overdue} follow-up{overdue > 1 ? "s" : ""} overdue
                <ChevronRight size={14} className="text-amber-500" />
              </button>
            )}
            {waitingOffer > 0 && (
              <button
                type="button"
                data-testid="owner-attention-waiting"
                onClick={() => openDrill(
                  "Waiting on your quote",
                  "You sent a price — nudge them to close.",
                  waitingList,
                )}
                className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-sm font-semibold text-amber-900 shadow-sm ring-1 ring-amber-200 hover:bg-amber-100"
              >
                {waitingOffer} {c.attentionWaitingQuote}
                <ChevronRight size={14} className="text-amber-500" />
              </button>
            )}
          </div>
        )}
      </Card>

      {/* This month — colourful summary cards */}
      <div className="mb-6">
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">This month</div>
        <div data-testid="owner-kpi-cards" className="grid grid-cols-2 gap-3">
          <KpiCard
            testId="kpi-new"
            icon={UserPlus}
            label="New enquiries"
            value={m.newThisMonth}
            last={m.newLastMonth}
            onClick={() => openDrill(
              "New enquiries this month",
              `${m.newThisMonth} ${m.newThisMonth === 1 ? "enquiry" : "enquiries"} came in this month.`,
              m.newThisList,
            )}
            tone={{ bg: "bg-sky-50", border: "border-sky-200", icon: "text-sky-600", iconBg: "bg-sky-100", value: "text-sky-900" }}
          />
          <KpiCard
            testId="kpi-signed"
            icon={CheckCircle2}
            label="Signed up"
            value={m.signedCountThis}
            last={m.signedCountLast}
            onClick={() => openDrill(
              "Signed up this month",
              `${m.signedCountThis} ${m.signedCountThis === 1 ? "deal" : "deals"} won this month. 🎉`,
              m.signedThisList,
            )}
            tone={{ bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-600", iconBg: "bg-emerald-100", value: "text-emerald-900" }}
          />
          <KpiCard
            testId="kpi-value"
            icon={IndianRupee}
            label="Value won /mo"
            value={formatINR(m.valueThisMonth)}
            rawValue={m.valueThisMonth}
            last={m.valueLastMonth}
            onClick={() => openDrill(
              "Value won this month",
              `${formatINR(m.valueThisMonth)} from ${m.signedCountThis} ${m.signedCountThis === 1 ? "deal" : "deals"} this month.`,
              m.signedThisList,
            )}
            tone={{ bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600", iconBg: "bg-amber-100", value: "text-amber-900" }}
          />
          <KpiCard
            testId="kpi-winrate"
            icon={Target}
            label="Win rate"
            value={winRatePct != null ? `${winRatePct}%` : "—"}
            rawValue={winRatePct}
            last={m.newLastMonth > 0 ? (m.signedCountLast / m.newLastMonth) * 100 : null}
            suffix="%"
            onClick={() => openDrill(
              "Win rate this month",
              winRatePct != null
                ? `${m.signedCountThis} of ${m.newThisMonth} new enquiries signed up — that's ${winRatePct}%.`
                : "No new enquiries yet this month.",
              m.newThisList,
            )}
            tone={{ bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600", iconBg: "bg-purple-100", value: "text-purple-900" }}
          />
        </div>
        <p data-testid="owner-last-month-line" className="mt-2 px-1 text-xs text-zinc-500">
          Last month: {m.newLastMonth} new · {m.signedCountLast} signed up · {formatINR(m.valueLastMonth)} value
        </p>
      </div>

      {/* Capacity snapshot — button 1 */}
      <OwnerCapacityStrip />

      {/* Renewal radar — button 7 */}
      <OwnerRenewalRadar />

      {/* Trend — enquiries vs signed up (same chart as operator desktop) */}
      <section className="mb-6">
        <SectionTitle testId="owner-section-trend">{c.sectionTrend}</SectionTitle>
        <Card>
          <div data-testid="owner-trend-chart">
            <TrendLineChart data={m.bars} height={200} />
          </div>
        </Card>
      </section>

      {/* Source donut */}
      <section className="mb-6">
        <SectionTitle testId="owner-section-sources">Where leads come from (30 days)</SectionTitle>
        <Card>
          {m.sourceData.length === 0 ? (
            <p className="text-sm text-zinc-500">No data yet.</p>
          ) : (
            <div data-testid="owner-source-donut" style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={m.sourceData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {m.sourceData.map((_, i) => (
                      <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </section>

      {/* Lost donut */}
      <section className="mb-6">
        <SectionTitle testId="owner-section-lost">Why we lose (30 days)</SectionTitle>
        <Card>
          {m.lostData.length === 0 ? (
            <p className="text-sm text-zinc-500">No losses in the last 30 days.</p>
          ) : (
            <div data-testid="owner-lost-donut" style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={m.lostData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {m.lostData.map((_, i) => (
                      <Cell key={i} fill={LOST_COLORS[i % LOST_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </section>

      {/* Recent wins */}
      <section>
        <SectionTitle testId="owner-section-wins">Recent wins</SectionTitle>
        <div className="space-y-2">
          {m.recentWins.length === 0 ? (
            <p className="rounded-xl border border-zinc-200 bg-white p-4 text-sm text-zinc-500">No wins yet.</p>
          ) : (
            m.recentWins.map((w) => (
              <button
                key={w.id}
                data-testid={`owner-win-${w.id}`}
                onClick={() => setSelectedId(w.id)}
                className="block w-full rounded-xl border border-zinc-200 bg-white p-4 text-left shadow-sm hover:bg-zinc-50"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-zinc-900">{w.name}</span>
                  <span className="text-sm font-semibold text-green-700">{formatINR(w.deal_value)}/mo</span>
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                  Closed by {w.closed_by || "—"} · {formatDate(w.signed_up_at)}
                </div>
              </button>
            ))
          )}
        </div>
      </section>

      <KpiDetailSheet
        drill={drill}
        onClose={() => setDrill(null)}
        onSelect={(id) => setSelectedId(id)}
      />
      <EnquiryDetailSheet enquiry={selected} onClose={() => setSelectedId(null)} />
    </div>
  );
};

// A colourful summary card with a month-over-month change indicator.
// rawValue is the numeric value used to compute the delta vs `last`
// (for formatted values like ₹ or %). Falls back to `value` when numeric.
const KpiCard = ({ testId, icon: Icon, label, value, rawValue, last, suffix = "", tone, onClick }) => {
  const current = rawValue != null ? rawValue : (typeof value === "number" ? value : null);
  let delta = null;
  if (current != null && last != null) delta = current - last;

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`rounded-2xl border ${tone.border} ${tone.bg} p-3.5 text-left shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md active:translate-y-0`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-xl ${tone.iconBg} ${tone.icon}`}>
          <Icon size={16} />
        </span>
        <DeltaChip delta={delta} suffix={suffix} />
      </div>
      <div className={`text-2xl font-semibold tracking-tight ${tone.value}`}>{value}</div>
      <div className="mt-0.5 flex items-center justify-between">
        <span className="text-xs font-medium text-zinc-500">{label}</span>
        <span className="inline-flex items-center text-[11px] font-medium text-zinc-400">
          View <ChevronRight size={12} />
        </span>
      </div>
    </button>
  );
};

// Drill-down list shown when a dashboard number is tapped. Lists the leads
// behind the figure; tapping one opens the full enquiry. Reused by every KPI
// card and the "Needs your attention" chips, so it works the same in both the
// embedded (Kreta) and standalone Jigyasa dashboards.
const KpiDetailSheet = ({ drill, onClose, onSelect }) => {
  const open = !!drill;
  return (
    <Sheet open={open} onClose={onClose} testId="owner-kpi-detail-sheet" title={drill?.title || ""}>
      {drill?.subtitle && (
        <p data-testid="owner-kpi-detail-subtitle" className="mb-3 text-sm text-zinc-600">{drill.subtitle}</p>
      )}
      {(!drill?.items || drill.items.length === 0) ? (
        <p data-testid="owner-kpi-detail-empty" className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-500">
          Nothing to show here yet.
        </p>
      ) : (
        <div data-testid="owner-kpi-detail-list" className="space-y-2">
          {drill.items.map((e) => (
            <EnquiryRow key={e.id} enquiry={e} onClick={() => onSelect(e.id)} showOverdue />
          ))}
        </div>
      )}
    </Sheet>
  );
};

const DeltaChip = ({ delta, suffix }) => {
  if (delta == null) return null;
  const rounded = Math.round(delta);
  if (rounded === 0) {
    return (
      <span data-testid="kpi-delta" className="inline-flex items-center gap-0.5 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-500">
        <Minus size={10} /> Same
      </span>
    );
  }
  const up = rounded > 0;
  const Arrow = up ? ArrowUp : ArrowDown;
  const cls = up ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700";
  return (
    <span data-testid="kpi-delta" className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>
      <Arrow size={10} /> {up ? "+" : ""}{rounded}{suffix}
    </span>
  );
};
